import { ExternalLink } from "lucide-mui";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useQuery } from "@tanstack/react-query";
import { keys } from "../../../api/queryKeys.ts";
import { api } from "../../../api/client.ts";
import type { JobAd } from "../../../api/client.ts";
import CountryName from "../../../components/CountryName.tsx";
import { DetailGridItem } from "../../../components/DetailRow.tsx";
import FormattedDate from "../../../components/FormattedDate.tsx";
import Panel from "../../../components/Panel.tsx";
import { SourceDataBlock } from "../../../components/detail/SourceDataCard.tsx";
import ExpandableRow from "../../../components/table/ExpandableRow.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import { ensureHttps } from "../../../utils/url.ts";

function JobAdRow({ ad }: { ad: JobAd }) {
  const websiteUrl = ensureHttps(ad.website);
  return (
    <ExpandableRow
      colSpan={6}
      cells={
        <>
          <TableCell>
            {ad.url ? (
              <Link
                href={ad.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
              >
                {ad.title} <ExternalLink fontSize="inherit" />
              </Link>
            ) : (
              ad.title
            )}
          </TableCell>
          <TableCell>{ad.source}</TableCell>
          <TableCell>
            {ad.city || ad.country ? (
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                {ad.city && <span>{ad.city}</span>}
                {ad.city && ad.country && <span>,</span>}
                {ad.country && <CountryName code={ad.country} />}
              </Box>
            ) : (
              "-"
            )}
          </TableCell>
          <TableCell>{ad.remote === true ? "Yes" : ad.remote === false ? "No" : "-"}</TableCell>
          <TableCell><FormattedDate value={ad.published_at} /></TableCell>
        </>
      }
    >
      <Grid container spacing={2}>
        <DetailGridItem label="Company name" value={ad.company_name} />
        <DetailGridItem
          label="Website"
          value={
            websiteUrl && (
              <Link
                href={websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
              >
                {ad.website} <ExternalLink fontSize="inherit" />
              </Link>
            )
          }
        />
        <DetailGridItem label="Match method" value={ad.match_method} />
        <DetailGridItem label="Match confidence" value={ad.match_confidence?.toFixed(2)} />
      </Grid>
      <SourceDataBlock data={ad.source_data} />
    </ExpandableRow>
  );
}

interface JobAdsSectionProps {
  companyId: string;
}

export default function JobAdsSection({ companyId }: JobAdsSectionProps) {
  const jobAds = useQuery({
    queryKey: keys.masterCompany.jobAds(companyId),
    queryFn: () => api.getCompanyJobAds(companyId),
    retry: false,
  });

  if (jobAds.isError || jobAds.isLoading || !jobAds.data?.length) {
    return null;
  }

  return (
    <Panel title="Job ads" count={jobAds.data.length} sx={{ mb: 3 }}>
      <ScrollTable>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>Title</TableCell>
            <TableCell>Source</TableCell>
            <TableCell>Location</TableCell>
            <TableCell>Remote</TableCell>
            <TableCell>Published</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {jobAds.data.map((ad) => (
            <JobAdRow key={ad.id} ad={ad} />
          ))}
        </TableBody>
      </ScrollTable>
    </Panel>
  );
}
